import { WaterlilyClient } from '@/api/waterlily';
import type {
  CalcSnapshot,
  CalcSnapshotDiff,
  CalcSnapshotRunAggregate,
  CalcSnapshotRunScope,
  CalcSnapshotRunStatus,
  CalcSnapshotTestCaseFacets,
  CalcSnapshotTestCaseStatus,
  SnapshotFieldDiff,
} from '@shared/calculations/snapshot-testing';

export const wizardCalcSnapshotKeys = {
  testCases: () => ['wizardCalcSnapshots', 'testCases'] as const,
  detail: (testCaseId: string, runResultId?: string | null) =>
    runResultId
      ? (['wizardCalcSnapshots', 'detail', testCaseId, runResultId] as const)
      : (['wizardCalcSnapshots', 'detail', testCaseId] as const),
  runs: () => ['wizardCalcSnapshots', 'runs'] as const,
  clients: (query: string) => ['wizardCalcSnapshots', 'clients', query] as const,
};

export interface WizardCalcSnapshotTestCase {
  testCaseId: string;
  advisorId: string;
  name: string;
  description: string;
  sourceClientId: string;
  sourcePartnerClientId: string | null;
  fundingSourceId: string;
  policyType: FundingPolicyType;
  strategyId: ProjectionStrategyId;
  facets: CalcSnapshotTestCaseFacets;
  tags: string[];
  asOfDate: string;
  revision: number;
  status: CalcSnapshotTestCaseStatus;
  createdBy: string;
  createdDateTime: string;
  updatedDateTime: string;
}

export interface WizardCalcSnapshotRevision {
  revisionId: string;
  testCaseId: string;
  revision: number;
  reason: string;
  changedBy: string;
  createdDateTime: string;
}

export interface WizardCalcSnapshotDetail {
  testCase: WizardCalcSnapshotTestCase & {
    expectedSnapshot: CalcSnapshot;
  };
  revisions: WizardCalcSnapshotRevision[];
  policySummary: {
    primaryLabel: string;
    secondaryLabel: string;
  };
  divergence: {
    hasDiverged: boolean;
    fields: SnapshotFieldDiff[];
    error?: string;
  };
  runResult: WizardCalcSnapshotRunResult | null;
  runExpectedSnapshot: CalcSnapshot | null;
}

export interface WizardCalcSnapshotRunResultSummary {
  runResultId: string;
  runId: string;
  testCaseId: string;
  passed: boolean;
  failureCount: number;
  warningCount: number;
  testCaseRevision: number;
  firstDivergentMonthIndex: number | null;
  createdDateTime: string;
}

export interface WizardCalcSnapshotRunResult extends WizardCalcSnapshotRunResultSummary {
  diff: CalcSnapshotDiff;
  actualSnapshot: CalcSnapshot | null;
}

export interface WizardCalcSnapshotRun {
  runId: string;
  advisorId: string;
  scope: CalcSnapshotRunScope;
  status: CalcSnapshotRunStatus;
  selectedTestCaseIds: string[];
  gitSha: string | null;
  triggeredBy: string;
  aggregate: CalcSnapshotRunAggregate;
  startedAt: string;
  completedAt: string | null;
  createdDateTime: string;
  results: WizardCalcSnapshotRunResultSummary[];
}

export interface WizardCalcSnapshotClientSearchResult {
  clientId: string;
  clientFirstName: string | null;
  clientLastName: string;
  clientEmail: string | null;
  ltcPolicyCount: number;
}

export interface CreateCalcSnapshotTestCaseRequest {
  name: string;
  description?: string;
  sourceClientId: string;
  fundingSourceId: string;
  asOfDate: string;
  tags?: string[];
}

export interface ReviseCalcSnapshotTestCaseRequest {
  mode: 'bless' | 'repin';
  reason: string;
}

export interface RunCalcSnapshotRequest {
  scope: CalcSnapshotRunScope;
  testCaseId?: string;
  tag?: string;
}

export const wizardCalcSnapshotsApi = {
  listTestCases: () =>
    WaterlilyClient.get<WizardCalcSnapshotTestCase[]>('/wizard-calc-snapshot-test-cases'),
  getTestCase: (testCaseId: string, runResultId?: string | null) =>
    WaterlilyClient.get<WizardCalcSnapshotDetail>(
      `/wizard-calc-snapshot-test-case/${testCaseId}`,
      runResultId ? { runResultId } : undefined,
    ),
  createTestCase: (body: CreateCalcSnapshotTestCaseRequest) =>
    WaterlilyClient.post<WizardCalcSnapshotTestCase>('/wizard-calc-snapshot-test-case', body),
  reviseTestCase: (testCaseId: string, body: ReviseCalcSnapshotTestCaseRequest) =>
    WaterlilyClient.put<WizardCalcSnapshotTestCase>(
      `/wizard-calc-snapshot-test-case/${testCaseId}`,
      body,
    ),
  run: (body: RunCalcSnapshotRequest) =>
    WaterlilyClient.post<{
      run: Omit<WizardCalcSnapshotRun, 'results'>;
      results: WizardCalcSnapshotRunResult[];
    }>('/wizard-calc-snapshot-run', body),
  listRuns: () => WaterlilyClient.get<WizardCalcSnapshotRun[]>('/wizard-calc-snapshot-runs'),
  searchClients: (query: string) =>
    WaterlilyClient.get<WizardCalcSnapshotClientSearchResult[]>('/wizard-calc-snapshot-clients', {
      query,
    }),
};
